import { useState, useEffect, useCallback } from 'react'
import { cmsService } from '../../services/cmsService'
import { Field, Input, Textarea, SectionCard, PageHeader, SpinnerIcon } from '../../components/cms/shared'
import RichTextEditor from '../../components/cms/RichTextEditor'

const PAGE = 'coverage'
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || 'https://tendercentrix.com'

const DEFAULTS = {
    hero: { title: 'Coverage', description: 'Clear boundaries on what we provide and what remains your responsibility.' },
    whatWeCover: { heading: 'What TenderCentriX Covers', subheading: 'We provide visibility of publicly advertised opportunities across the full spectrum of regulated care.', categories: [] },
    whatWeDontCover: { heading: 'What TenderCentriX Does Not Cover', subheading: 'Explicit exclusions from our service.', items: [] },
    howItWorks: { heading: 'How Coverage Works', items: [] },
    yourResponsibility: { heading: 'Your Responsibility as a Provider', intro: '', mustDoLabel: 'You must:', mustDoItems: [] },
}

function initState() {
    return {
        hero: { saving: false, saved: false, error: null },
        whatWeCover: { saving: false, saved: false, error: null },
        whatWeDontCover: { saving: false, saved: false, error: null },
        howItWorks: { saving: false, saved: false, error: null },
        yourResponsibility: { saving: false, saved: false, error: null },
    }
}

function ItemListEditor({ items, onChange, placeholder = 'Enter item...' }) {
    const update = (i, v) => { const a = [...items]; a[i] = v; onChange(a) }
    const add = () => onChange([...items, ''])
    const remove = (i) => { const a = [...items]; a.splice(i, 1); onChange(a) }
    return (
        <div className="space-y-2">
            {items.map((item, i) => (
                <div key={i} className="flex gap-2">
                    <input
                        type="text"
                        value={item}
                        onChange={(e) => update(i, e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 px-3 py-1.5 text-sm bg-white border border-admin-border rounded text-admin-text focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                    />
                    <button onClick={() => remove(i)} className="px-2 text-red-400 hover:text-red-600 text-xs font-bold">✕</button>
                </div>
            ))}
            <button onClick={add} className="text-xs font-bold text-brand-navy border border-dashed border-brand-navy/40 rounded px-3 py-1 hover:bg-brand-navy/5 transition-all">
                + Add Item
            </button>
        </div>
    )
}

export default function CoverageCmsPage() {
    const [content, setContent] = useState(DEFAULTS)
    const [pageLoading, setPageLoading] = useState(true)
    const [sectionState, setSectionState] = useState(initState())

    useEffect(() => {
        cmsService.getPage(PAGE)
            .then((res) => { if (res?.data) setContent({ ...DEFAULTS, ...res.data }) })
            .catch(() => {})
            .finally(() => setPageLoading(false))
    }, [])

    const setSaving = (section, state) =>
        setSectionState((prev) => ({ ...prev, [section]: { ...prev[section], ...state } }))

    const save = useCallback(async (section, payload) => {
        setSaving(section, { saving: true, saved: false, error: null })
        try {
            await cmsService.updateSection(PAGE, section, payload)
            setSaving(section, { saving: false, saved: true, error: null })
            setTimeout(() => setSaving(section, { saved: false }), 3000)
        } catch (err) {
            setSaving(section, { saving: false, saved: false, error: err.message || 'Save failed' })
        }
    }, [])

    const set = (key, val) => setContent((c) => ({ ...c, [key]: val }))
    const setNested = (section, key, val) => setContent((c) => ({ ...c, [section]: { ...c[section], [key]: val } }))

    const updateCategory = (i, field, value) => {
        const cats = content.whatWeCover.categories.map((c, idx) => idx === i ? { ...c, [field]: value } : c)
        setNested('whatWeCover', 'categories', cats)
    }
    const updateCategoryItems = (i, items) => {
        const cats = content.whatWeCover.categories.map((c, idx) => idx === i ? { ...c, items } : c)
        setNested('whatWeCover', 'categories', cats)
    }
    const addCategory = () => {
        setNested('whatWeCover', 'categories', [...(content.whatWeCover.categories || []), { title: '', items: [] }])
    }
    const removeCategory = (i) => {
        const cats = (content.whatWeCover.categories || []).filter((_, idx) => idx !== i)
        setNested('whatWeCover', 'categories', cats)
    }
    const moveCategory = (i, direction) => {
        const cats = [...(content.whatWeCover.categories || [])]
        const target = i + direction
        if (target < 0 || target >= cats.length) return
        ;[cats[i], cats[target]] = [cats[target], cats[i]]
        setNested('whatWeCover', 'categories', cats)
    }

    if (pageLoading) return <div className="flex items-center justify-center py-24"><SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" /></div>

    return (
        <div className="space-y-6">
            <PageHeader title="Coverage — Content Editor" description="Edit Coverage page sections and care service category lists." liveUrl={`${FRONTEND_URL}/coverage`} />

            <SectionCard title="Hero Banner" onSave={() => save('hero', content.hero)} {...sectionState.hero}>
                <Field label="Page Title"><Input value={content.hero.title} onChange={(v) => setNested('hero', 'title', v)} /></Field>
                <Field label="Description"><RichTextEditor value={content.hero.description} onChange={(v) => setNested('hero', 'description', v)} /></Field>
            </SectionCard>

            <SectionCard title="What We Cover" description="Section heading and service categories." onSave={() => save('whatWeCover', content.whatWeCover)} {...sectionState.whatWeCover}>
                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Section Heading"><Input value={content.whatWeCover.heading} onChange={(v) => setNested('whatWeCover', 'heading', v)} /></Field>
                    <Field label="Section Sub-heading"><Input value={content.whatWeCover.subheading} onChange={(v) => setNested('whatWeCover', 'subheading', v)} /></Field>
                </div>
                <div className="border-t border-admin-border pt-4 space-y-4">
                    <p className="text-xs font-bold text-admin-muted uppercase tracking-wider">Service Categories</p>
                    {(content.whatWeCover.categories || []).map((cat, i) => (
                        <div key={i} className="p-4 bg-slate-50 border border-admin-border rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] font-bold text-admin-muted uppercase tracking-widest">Category {i + 1}</p>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => moveCategory(i, -1)}
                                        disabled={i === 0}
                                        className="text-[10px] text-admin-muted hover:text-brand-navy font-bold disabled:opacity-30 disabled:hover:text-admin-muted"
                                    >
                                        ↑ Move Up
                                    </button>
                                    <button
                                        onClick={() => moveCategory(i, 1)}
                                        disabled={i === (content.whatWeCover.categories || []).length - 1}
                                        className="text-[10px] text-admin-muted hover:text-brand-navy font-bold disabled:opacity-30 disabled:hover:text-admin-muted"
                                    >
                                        ↓ Move Down
                                    </button>
                                    <button onClick={() => removeCategory(i)} className="text-[10px] text-red-400 hover:text-red-600 font-bold">Remove Category</button>
                                </div>
                            </div>
                            <Field label="Category Title">
                                <Input value={cat.title} onChange={(v) => updateCategory(i, 'title', v)} />
                            </Field>
                            <Field label="Items (one per line)">
                                <ItemListEditor items={cat.items || []} onChange={(items) => updateCategoryItems(i, items)} />
                            </Field>
                        </div>
                    ))}
                    <button onClick={addCategory} className="text-xs font-bold text-brand-navy border border-dashed border-brand-navy/40 rounded px-3 py-1 hover:bg-brand-navy/5 transition-all">
                        + Add Category
                    </button>
                </div>
            </SectionCard>

            <SectionCard title="What We Don't Cover" onSave={() => save('whatWeDontCover', content.whatWeDontCover)} {...sectionState.whatWeDontCover}>
                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Section Heading"><Input value={content.whatWeDontCover.heading} onChange={(v) => setNested('whatWeDontCover', 'heading', v)} /></Field>
                    <Field label="Sub-heading"><Input value={content.whatWeDontCover.subheading} onChange={(v) => setNested('whatWeDontCover', 'subheading', v)} /></Field>
                </div>
                <Field label="Excluded Items">
                    <ItemListEditor items={content.whatWeDontCover.items || []} onChange={(items) => setNested('whatWeDontCover', 'items', items)} />
                </Field>
            </SectionCard>

            <SectionCard title="How Coverage Works" onSave={() => save('howItWorks', content.howItWorks)} {...sectionState.howItWorks}>
                <Field label="Section Heading"><Input value={content.howItWorks.heading} onChange={(v) => setNested('howItWorks', 'heading', v)} /></Field>
                <Field label="How It Works Items">
                    <ItemListEditor items={content.howItWorks.items || []} onChange={(items) => setNested('howItWorks', 'items', items)} />
                </Field>
            </SectionCard>

            <SectionCard title="Your Responsibility" onSave={() => save('yourResponsibility', content.yourResponsibility)} {...sectionState.yourResponsibility}>
                <Field label="Section Heading"><Input value={content.yourResponsibility.heading} onChange={(v) => setNested('yourResponsibility', 'heading', v)} /></Field>
                <Field label="Intro Paragraph"><RichTextEditor value={content.yourResponsibility.intro} onChange={(v) => setNested('yourResponsibility', 'intro', v)} /></Field>
                <Field label="Must-Do Label"><Input value={content.yourResponsibility.mustDoLabel} onChange={(v) => setNested('yourResponsibility', 'mustDoLabel', v)} /></Field>
                <Field label="Must-Do Items">
                    <ItemListEditor items={content.yourResponsibility.mustDoItems || []} onChange={(items) => setNested('yourResponsibility', 'mustDoItems', items)} />
                </Field>
            </SectionCard>
        </div>
    )
}
